import { motion } from "motion/react";
import { Cloud, Server, AlertTriangle, RefreshCw } from "lucide-react";
import { Component, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "../../components/ui/button";
import { G360NavTabs, type G360NavTab } from "../itCompliance/components/G360NavTabs";
import { PrivilegeAccessInsights } from "./PrivilegeAccessInsights";
import { NetworkGraph } from "./components/NetworkGraph";
import { PagFilterBar } from "./components/PagFilterBar";
import { PagReviewTable } from "./components/PagReviewTable";
import { PolicyInsightsGraph } from "./components/PolicyInsightsGraph";
import {
  buildPagNetworkModel,
  buildPagTree,
  type PagApplication,
  type PagCycle,
  type PagFilters,
} from "./data/pagTypes";
import { fetchPagNetworkGraphData } from "./services/pagApi";

/** Top-level split: the policy tree / network graph describe cloud access,
 *  while on-prem privileged access is covered by the Insights view. */
type DeploymentTab = "onprem" | "cloud";

const DEPLOYMENT_TABS: G360NavTab<DeploymentTab>[] = [
  { key: "onprem", label: "On-Premises Resources", icon: Server },
  { key: "cloud", label: "Cloud Resources", icon: Cloud },
];

type GraphTab = "tree" | "network";

const GRAPH_TABS: { id: GraphTab; label: string }[] = [
  { id: "tree", label: "Policy Insights" },
  { id: "network", label: "Network Graph" },
];
const PANEL_HIDDEN = "pointer-events-none opacity-0 absolute inset-x-0 top-0";

const INITIAL_FILTERS: PagFilters = {
  departments: [],
  divisions: [],
  applications: [],
};

export class CloudViewErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("CloudViewErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="rounded-xl border border-red-500/20 bg-card p-8 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/15 flex items-center justify-center">
            <AlertTriangle className="size-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-base font-bold text-foreground">
            Could not load Cloud Resources
          </h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            {this.state.error.message ||
              "An unexpected error occurred while preparing the cloud resources view."}
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <Button
              variant="indigo"
              size="sm"
              onClick={() => {
                this.setState({ error: null });
                this.props.onReset?.();
              }}
              className="gap-1.5"
            >
              <RefreshCw className="size-3.5" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function UserAccessGovernance() {
  const [deployment, setDeployment] = useState<DeploymentTab>("onprem");
  const [filters, setFilters] = useState<PagFilters>(INITIAL_FILTERS);
  const [cycle, setCycle] = useState<PagCycle>("current");
  const [graphTab, setGraphTab] = useState<GraphTab>("tree");

  const [filtersReady, setFiltersReady] = useState(false);

  const [reloadToken, setReloadToken] = useState(0);

  const applyFilters = useCallback((next: PagFilters) => {
    setFilters(next);
    setReloadToken((token) => token + 1);
  }, []);

  const [graphApps, setGraphApps] = useState<PagApplication[]>([]);
  const [graphLoading, setGraphLoading] = useState(true);


  const appCodes = filters.applications;
  const appCodesKey = appCodes.join(",");

  useEffect(() => {
    if (deployment !== "cloud") return;
    if (!filtersReady) return;
    if (appCodes.length === 0) {
      setGraphApps([]);
      setGraphLoading(false);
      return;
    }
    let cancelled = false;
    setGraphLoading(true);

    fetchPagNetworkGraphData(appCodes)
      .then((res) => {
        if (!cancelled) setGraphApps(res?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setGraphApps([]);
      })
      .finally(() => {
        if (!cancelled) setGraphLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appCodesKey, filtersReady, reloadToken, deployment]);

  const treeNodes = useMemo(() => buildPagTree(graphApps), [graphApps]);
  const networkModel = useMemo(() => buildPagNetworkModel(graphApps), [graphApps]);

  return (
    <div className="space-y-4">
      {/* ── Cloud / On Prem ────────────────────────────────────────── */}
      <G360NavTabs
        tabs={DEPLOYMENT_TABS}
        activeTab={deployment}
        onTabChange={setDeployment}
        ariaLabel="Deployment"
        size="lg"
      />

      {deployment === "onprem" ? (
        <PrivilegeAccessInsights />
      ) : (
        <CloudViewErrorBoundary onReset={() => setReloadToken((t) => t + 1)}>
          {/* ── Filters ────────────────────────────────────────────────── */}
          <PagFilterBar
            applied={filters}
            onApply={applyFilters}
            onReady={() => setFiltersReady(true)}
          />

          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            <div className="rounded-xl border border-border bg-card p-5 pt-3">
              <div className="mb-3 flex items-center gap-5 border-b border-border">
                {GRAPH_TABS.map((t) => (
                  <Button variant="ghost"
                    key={t.id}
                    onClick={() => setGraphTab(t.id)}
                    className={`justify-start h-auto px-0 py-0 font-normal border-b-2 pb-1.5 text-sm transition-colors ${graphTab === t.id
                        ? "border-indigo-500 font-semibold text-foreground"
                        : "border-transparent font-medium text-muted-foreground hover:text-foreground"
                      }`}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>

              <div className="relative">
                <div className={graphTab === "tree" ? "" : PANEL_HIDDEN}>
                  <PolicyInsightsGraph treeNodes={treeNodes} loading={graphLoading} />
                </div>
                <div className={graphTab === "network" ? "" : PANEL_HIDDEN}>
                  <NetworkGraph model={networkModel} loading={graphLoading} />
                </div>
              </div>
            </div>

            {/* ── Details table ────────────────────────────────────────── */}
            <PagReviewTable
              appCodes={appCodes}
              ready={filtersReady}
              cycle={cycle}
              onCycleChange={setCycle}
              refreshToken={reloadToken}
            />
          </motion.div>
        </CloudViewErrorBoundary>
      )}
    </div>
  );
}
